import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gauge, Fuel, StickyNote, CheckCircle2, X, Loader, Droplet, Ban, Target, GraduationCap, PlaneLanding, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api, { hobbsService } from '../services/apiService';

// PR #5 — Libro de vuelo
const PURPOSE_OPTIONS = [
  { value: 'ENT',       label: 'Entrenamiento (ENT)' },
  { value: 'VP',        label: 'Vuelo personal (VP)' },
  { value: 'ADAP',      label: 'Adaptación (ADAP)' },
  { value: 'READAP',    label: 'Readaptación (READAP)' },
  { value: 'INST',      label: 'Instrucción (INST)' },
  { value: 'Bautismo',  label: 'Bautismo' },
  { value: 'Excursion', label: 'Excursión' },
  { value: 'Otros',     label: 'Otros' },
];
const PAYMENT_OPTIONS = ['Transferencia', 'Cuenta corriente', 'Pagado', 'Paquete', 'Efectivo'];

const PostFlightModal = ({ record, darkMode, onComplete, onDismiss }) => {
  const { t } = useTranslation();
  const [hobbsEnd, setHobbsEnd] = useState('');
  const [fuel, setFuel]         = useState('');
  const [notes, setNotes]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [saved, setSaved]         = useState(false);

  // Anular vuelo (acción destructiva — confirma con motivo obligatorio)
  const [annulOpen, setAnnulOpen]       = useState(false);
  const [annulReason, setAnnulReason]   = useState('');
  const [annulLoading, setAnnulLoading] = useState(false);
  const [annulError, setAnnulError]     = useState('');

  // Oil tracking
  const [oilEndQts, setOilEndQts]     = useState('');
  const [oilAddedQts, setOilAddedQts] = useState('');
  const [oilItemId, setOilItemId]     = useState('');
  const [oilItems, setOilItems]       = useState([]);

  // Fuel tracking (post-vuelo: fuel_end_left/right)
  const [fuelEndLeft,  setFuelEndLeft]  = useState('');
  const [fuelEndRight, setFuelEndRight] = useState('');
  const [fuelError,    setFuelError]    = useState('');

  // PR #5 — Libro de vuelo
  const [purpose, setPurpose]                 = useState('');
  const [purposeOther, setPurposeOther]       = useState('');
  const [instructors, setInstructors]         = useState([]);
  const [instructorMode, setInstructorMode]   = useState('list'); // 'list' | 'free'
  const [instructorUserId, setInstructorUserId] = useState('');
  const [instructorName, setInstructorName]   = useState('');
  const [landings, setLandings]               = useState('');
  const [paymentMethod, setPaymentMethod]     = useState('');

  const fuelStartLeft   = record?.fuel_start_left   != null ? parseFloat(record.fuel_start_left)   : null;
  const fuelStartRight  = record?.fuel_start_right  != null ? parseFloat(record.fuel_start_right)  : null;
  const fuelCapLeft     = record?.fuel_capacity_left  != null ? parseFloat(record.fuel_capacity_left)  : null;
  const fuelCapRight    = record?.fuel_capacity_right != null ? parseFloat(record.fuel_capacity_right) : null;
  const singleTank      = record?.single_tank === true;
  const fuelUnit        = record?.fuel_unit || 'L';
  const hasFuelTracking = fuelStartLeft != null || fuelStartRight != null;

  useEffect(() => {
    let cancelled = false;
    async function fetchOilItems() {
      try {
        const API    = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const stored = localStorage.getItem('condor360_current_user') || '{}';
        const token  = JSON.parse(stored).token || '';
        const res = await fetch(`${API}/api/inventory-items/oils`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const json = await res.json();
        if (!cancelled) setOilItems(Array.isArray(json?.data) ? json.data : []);
      } catch (_) {
        if (!cancelled) setOilItems([]);
      }
    }
    fetchOilItems();
    return () => { cancelled = true; };
  }, []);

  // PR #5 — Cargar lista de instructores de la org (endpoint acotado:
  // accesible a cualquier rol autenticado, devuelve sólo {id, name}).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/users/instructors');
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (!cancelled) setInstructors(list);
      } catch (_) {
        if (!cancelled) setInstructors([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Limpiar estado de rellenado cuando la sección no aplica
  // (evita valores residuales si el piloto cambia el "Aceite actual")
  useEffect(() => {
    const oilStartNum = record?.oil_start_qts != null ? parseFloat(record.oil_start_qts) : null;
    const oilEndNum   = oilEndQts !== '' ? parseFloat(oilEndQts) : null;
    const showRefill  = oilStartNum != null && oilEndNum != null && !isNaN(oilEndNum) && oilEndNum < oilStartNum;
    if (!showRefill) {
      setOilAddedQts('');
      setOilItemId('');
    }
  }, [oilEndQts, record?.oil_start_qts]);

  const acName = record
    ? `${record.manufacturer} ${record.model_name}${record.registration ? ' (' + record.registration + ')' : ''}`
    : '';

  const handleSubmit = async () => {
    setError('');
    const end = parseFloat(hobbsEnd);
    if (!hobbsEnd || isNaN(end)) { setError(t('hobbs_end_required',{ns:'admin'})); return; }
    if (record?.hobbs_start != null && end < parseFloat(record.hobbs_start)) {
      setError(t('hobbs_end_invalid',{ns:'admin'}));
      return;
    }

    const oilEnd   = oilEndQts   !== '' ? parseFloat(oilEndQts)   : null;
    // Defensivo: solo enviar oilAdded si la sección de rellenado realmente aplica
    const oilStartNum = record?.oil_start_qts != null ? parseFloat(record.oil_start_qts) : null;
    const oilEndNum   = oilEndQts !== '' ? parseFloat(oilEndQts) : null;
    const showRefill  = oilStartNum != null && oilEndNum != null && !isNaN(oilEndNum) && oilEndNum < oilStartNum;
    const oilAddedParsed = oilAddedQts !== '' ? parseFloat(oilAddedQts) : null;
    const oilAdded = (showRefill && oilAddedParsed != null && !isNaN(oilAddedParsed) && oilAddedParsed > 0)
      ? oilAddedParsed
      : null; 
    const oilItem  = oilItemId || null;

    if (oilAdded != null && oilAdded > 0 && !oilItem) {
      setError('Si rellenás aceite, debés seleccionar el tipo del inventario');
      return;
    }

    // ── PR #5: Libro de vuelo ──────────────────────────────────
    if (!purpose) {
      setError('La finalidad del vuelo es requerida');
      return;
    }
    if (purpose === 'Otros' && !purposeOther.trim()) {
      setError('Indicá la finalidad cuando seleccionás "Otros"');
      return;
    }
    // Instructor opcional en todas las finalidades — la única validación es la
    // coherencia (mutuamente excluyente) que aplica el backend.
    const landingsNum = landings !== '' ? parseInt(landings, 10) : NaN;
    if (isNaN(landingsNum) || landingsNum < 0) {
      setError('La cantidad de aterrizajes es requerida (entero ≥ 0)');
      return;
    }
    if (!paymentMethod) {
      setError('La forma de pago es requerida');
      return;
    }

    // Validación combustible final (solo si hay tracking)
    let fuelEndLeftNum  = null;
    let fuelEndRightNum = null;
    if (hasFuelTracking) {
      fuelEndLeftNum  = fuelEndLeft  !== '' ? parseFloat(fuelEndLeft)  : null;
      fuelEndRightNum = !singleTank && fuelEndRight !== '' ? parseFloat(fuelEndRight) : null;
      if (fuelEndLeftNum != null && (isNaN(fuelEndLeftNum) || fuelEndLeftNum < 0)) {
        setFuelError('Valor inválido para el tanque izquierdo'); return;
      }
      if (fuelEndRightNum != null && (isNaN(fuelEndRightNum) || fuelEndRightNum < 0)) {
        setFuelError('Valor inválido para el tanque derecho'); return;
      }
      if (fuelEndLeftNum != null && fuelCapLeft != null && fuelEndLeftNum > fuelCapLeft) {
        setFuelError(`Excede la capacidad del tanque izquierdo (${fuelCapLeft} ${fuelUnit})`); return;
      }
      if (fuelEndRightNum != null && fuelCapRight != null && fuelEndRightNum > fuelCapRight) {
        setFuelError(`Excede la capacidad del tanque derecho (${fuelCapRight} ${fuelUnit})`); return;
      }
      setFuelError('');
    }

    setLoading(true);
    try {
      const body = {
        hobbs_end:              end,
        notes:                  notes || null,
        oil_end_qts:            oilEnd,
        oil_added_qts:          oilAdded,
        oil_inventory_item_id:  oilItem,
        // PR #5 — Libro de vuelo
        purpose,
        purpose_other:        purpose === 'Otros' ? purposeOther.trim() : null,
        instructor_user_id:   instructorMode === 'list' ? (instructorUserId || null) : null,
        instructor_name:      instructorMode === 'free' ? (instructorName.trim() || null) : null,
        landings:             landingsNum,
        payment_method:       paymentMethod,
      };
      if (hasFuelTracking) {
        if (fuelEndLeftNum  != null) body.fuel_end_left  = fuelEndLeftNum;
        if (fuelEndRightNum != null) body.fuel_end_right = fuelEndRightNum;
      } else {
        body.fuel_remaining = fuel !== '' ? parseFloat(fuel) : null;
      }
      const res = await api.patch(`/api/aircraft/hobbs/${record.id}/complete`, body);
      setSaved(true);
      setTimeout(() => onComplete(res.data), 1800);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnnul = async () => {
    setAnnulError('');
    const reason = annulReason.trim();
    if (!reason) { setAnnulError('El motivo es obligatorio'); return; }
    setAnnulLoading(true);
    try {
      await hobbsService.annul(record.id, reason);
      onComplete({ annulled: true });
    } catch (err) {
      setAnnulError(err?.response?.data?.message || 'Error al anular el vuelo');
    } finally {
      setAnnulLoading(false);
    }
  };

  const bg   = darkMode ? '#111827' : 'white';
  const text = darkMode ? 'white' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 12, fontSize: 15,
    fontWeight: 600, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    color: text,
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,0.6)',
                 backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center',
                 justifyContent: 'center', padding: 16 }}>
        <motion.div
          initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
          style={{ width: '100%', maxWidth: 420, borderRadius: 24, background: bg,
                   boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                   border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                   maxHeight: '90vh', overflowY: 'auto' }}>

          {/* Header */}
          <div style={{ padding: '20px 24px 16px',
                        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 14,
                            background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Gauge style={{ width: 20, height: 20, color: 'white' }} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>Registro post-vuelo</h2>
                <p style={{ margin: 0, fontSize: 12, color: sub }}>{acName}</p>
              </div>
            </div>
            <button onClick={onDismiss}
              style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', border: 'none',
                       borderRadius: 10, width: 34, height: 34, cursor: 'pointer',
                       display: 'flex', alignItems: 'center', justifyContent: 'center', color: sub }}>
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>

          {/* Referencia hobbs inicio */}
          <div style={{ margin: '16px 24px 0', padding: '10px 14px', borderRadius: 12,
                        background: darkMode ? 'rgba(37,99,235,0.1)' : 'rgba(219,234,254,0.6)',
                        border: darkMode ? '1px solid rgba(37,99,235,0.2)' : '1px solid rgba(37,99,235,0.15)',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: darkMode ? '#93c5fd' : '#2563eb' }}>
              Horómetro al salir
            </span>
            <span style={{ fontSize: 16, fontWeight: 900, color: darkMode ? '#93c5fd' : '#1d4ed8' }}>
              {record?.hobbs_start != null ? `${record.hobbs_start} hs` : '—'}
            </span>
          </div>

          {/* Campos */}
          <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                              display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <Gauge style={{ width: 13, height: 13 }} /> Horómetro al aterrizar *
              </label>
              <input type="number" step="0.1"
                min={record?.hobbs_start != null ? record.hobbs_start : undefined}
                placeholder={record?.hobbs_start != null
                  ? `Ej: ${(parseFloat(record.hobbs_start) + 1.5).toFixed(1)}`
                  : 'Ej: 1235.7'}
                value={hobbsEnd} onChange={e => setHobbsEnd(e.target.value)}
                style={inputStyle} />
            </div>

            {!hasFuelTracking && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                                display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <Fuel style={{ width: 13, height: 13 }} /> Combustible restante (litros) — opcional
                </label>
                <input type="number" step="1" min="0" placeholder="Ej: 80"
                  value={fuel} onChange={e => setFuel(e.target.value)}
                  style={inputStyle} />
              </div>
            )}

            {hasFuelTracking && (
              <>
                {/* Combustible de salida (lectura) */}
                <div style={{ padding: '10px 14px', borderRadius: 12,
                              background: darkMode ? 'rgba(16,185,129,0.10)' : 'rgba(209,250,229,0.5)',
                              border: darkMode ? '1px solid rgba(16,185,129,0.2)' : '1px solid rgba(16,185,129,0.18)',
                              display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? '#6ee7b7' : '#047857' }}>
                    ⛽ Combustible de salida
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: darkMode ? '#a7f3d0' : '#065f46' }}>
                    {singleTank
                      ? `${fuelStartLeft != null ? fuelStartLeft : '—'} ${fuelUnit}`
                      : `Izq ${fuelStartLeft != null ? fuelStartLeft : '—'} · Der ${fuelStartRight != null ? fuelStartRight : '—'} ${fuelUnit}`}
                  </span>
                </div>

                {/* Combustible remanente (inputs) */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                                  display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                    <Fuel style={{ width: 13, height: 13 }} /> Combustible remanente — opcional
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <input type="number" step="0.1" min="0"
                        placeholder={singleTank ? `${fuelUnit}` : `Izq (${fuelUnit})`}
                        value={fuelEndLeft}
                        onChange={e => { setFuelEndLeft(e.target.value); setFuelError(''); }}
                        style={inputStyle} />
                    </div>
                    {!singleTank && (
                      <div style={{ flex: 1 }}>
                        <input type="number" step="0.1" min="0"
                          placeholder={`Der (${fuelUnit})`}
                          value={fuelEndRight}
                          onChange={e => { setFuelEndRight(e.target.value); setFuelError(''); }}
                          style={inputStyle} />
                      </div>
                    )}
                  </div>
                  {(() => {
                    const endL = fuelEndLeft  !== '' ? parseFloat(fuelEndLeft)  : null;
                    const endR = fuelEndRight !== '' ? parseFloat(fuelEndRight) : null;
                    const startTotal = (fuelStartLeft || 0) + (singleTank ? 0 : (fuelStartRight || 0));
                    const endTotal   = (endL || 0) + (singleTank ? 0 : (endR || 0));
                    if ((endL == null && endR == null) || startTotal === 0) return null;
                    const used = Math.max(0, startTotal - endTotal);
                    return (
                      <p style={{ margin: '6px 2px 0', fontSize: 11, fontWeight: 600,
                                  color: darkMode ? 'rgba(110,231,183,0.85)' : '#047857' }}>
                        Consumo estimado: {used.toFixed(1)} {fuelUnit}
                      </p>
                    );
                  })()}
                  {fuelError && (
                    <p style={{ margin: '6px 2px 0', fontSize: 11, color: '#ef4444', fontWeight: 600 }}>
                      ⚠ {fuelError}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Aceite inicial (lectura) */}
            {record?.oil_start_qts != null && (
              <div style={{ padding: '10px 14px', borderRadius: 12,
                            background: darkMode ? 'rgba(245,158,11,0.1)' : 'rgba(254,243,199,0.6)',
                            border: darkMode ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(245,158,11,0.15)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: darkMode ? '#fcd34d' : '#b45309' }}>
                  Aceite inicial
                </span>
                <span style={{ fontSize: 16, fontWeight: 900, color: darkMode ? '#fcd34d' : '#92400e' }}>
                  {record.oil_start_qts} qt
                </span>
              </div>
            )}

            {/* Aceite actual */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                              display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <Droplet style={{ width: 13, height: 13 }} /> Aceite actual (qt) — opcional
              </label>
              <input type="number" step="0.1" min="0" max="99" placeholder="ej: 6.0"
                value={oilEndQts} onChange={e => setOilEndQts(e.target.value)}
                style={inputStyle} />
            </div>

            {/* Sugerencia de rellenado */}
            {record?.oil_start_qts != null
              && oilEndQts !== ''
              && !isNaN(parseFloat(oilEndQts))
              && parseFloat(oilEndQts) < parseFloat(record.oil_start_qts) && (
              <div style={{ padding: '12px 14px', borderRadius: 12,
                            background: darkMode ? 'rgba(99,102,241,0.08)' : 'rgba(224,231,255,0.5)',
                            border: darkMode ? '1px solid rgba(99,102,241,0.25)' : '1px solid rgba(99,102,241,0.2)',
                            display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700,
                            color: darkMode ? '#a5b4fc' : '#4338ca' }}>
                  Te recomendamos rellenar para volver a {record.oil_start_qts} qt
                </p>

                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: sub,
                                  display: 'block', marginBottom: 4 }}>
                    Cantidad a rellenar (qt)
                  </label>
                  <input type="number" step="0.1" min="0" max="99"
                    value={oilAddedQts}
                    onChange={e => setOilAddedQts(e.target.value)}
                    placeholder={(parseFloat(record.oil_start_qts) - parseFloat(oilEndQts)).toFixed(1)}
                    style={inputStyle} />
                </div>

                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: sub,
                                  display: 'block', marginBottom: 4 }}>
                    Tipo de aceite
                  </label>
                  <select value={oilItemId} onChange={e => setOilItemId(e.target.value)}
                    style={inputStyle}>
                    <option value="">— Seleccioná un aceite —</option>
                    {oilItems.map(it => (
                      <option key={it.id} value={it.id}>
                        {it.name} ({it.current_stock_quantity} {it.stock_unit})
                      </option>
                    ))}
                  </select>
                  {oilItems.length === 0 && (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: sub, fontStyle: 'italic' }}>
                      No hay aceites en el inventario
                    </p>
                  )}
                </div>

                {oilItemId && oilAddedQts && !isNaN(parseFloat(oilAddedQts)) && (() => {
                  const it = oilItems.find(x => x.id === oilItemId);
                  if (!it) return null;
                  return (
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 600,
                                color: darkMode ? 'rgba(165,180,252,0.85)' : '#4f46e5' }}>
                      Se descontará {oilAddedQts} {it.stock_unit || 'unidades'} del stock de {it.name}
                    </p>
                  );
                })()}
              </div>
            )}

            {/* PR #5 — Libro de vuelo */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                              display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <Target style={{ width: 13, height: 13 }} /> Finalidad del vuelo *
              </label>
              <select value={purpose} onChange={e => setPurpose(e.target.value)} style={inputStyle}>
                <option value="">— Seleccioná la finalidad —</option>
                {PURPOSE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {purpose === 'Otros' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                                display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  Especificá la finalidad *
                </label>
                <input type="text" placeholder="Ej: Traslado para inspección"
                  value={purposeOther} onChange={e => setPurposeOther(e.target.value)}
                  style={inputStyle} />
              </div>
            )}

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                              display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <GraduationCap style={{ width: 13, height: 13 }} />
                Instructor — opcional
              </label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <button type="button"
                  onClick={() => { setInstructorMode('list'); setInstructorName(''); }}
                  style={{
                    flex: 1, padding: '7px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    fontFamily: 'inherit', cursor: 'pointer',
                    background: instructorMode === 'list'
                      ? 'linear-gradient(135deg,#2563eb,#1d4ed8)'
                      : (darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9'),
                    color: instructorMode === 'list' ? 'white' : sub,
                    border: 'none',
                  }}>
                  De la lista
                </button>
                <button type="button"
                  onClick={() => { setInstructorMode('free'); setInstructorUserId(''); }}
                  style={{
                    flex: 1, padding: '7px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    fontFamily: 'inherit', cursor: 'pointer',
                    background: instructorMode === 'free'
                      ? 'linear-gradient(135deg,#2563eb,#1d4ed8)'
                      : (darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9'),
                    color: instructorMode === 'free' ? 'white' : sub,
                    border: 'none',
                  }}>
                  Escribir nombre
                </button>
              </div>
              {instructorMode === 'list' ? (
                <select value={instructorUserId}
                  onChange={e => setInstructorUserId(e.target.value)} style={inputStyle}>
                  <option value="">— Seleccioná un instructor —</option>
                  {instructors.map(it => (
                    <option key={it.id} value={it.id}>{it.name}</option>
                  ))}
                </select>
              ) : (
                <input type="text" placeholder="Nombre del instructor"
                  value={instructorName} onChange={e => setInstructorName(e.target.value)}
                  style={inputStyle} />
              )}
              {instructorMode === 'list' && instructors.length === 0 && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: sub, fontStyle: 'italic' }}>
                  No hay instructores cargados. Usá "Escribir nombre".
                </p>
              )}
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                              display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <PlaneLanding style={{ width: 13, height: 13 }} /> Aterrizajes *
              </label>
              <input type="number" min="0" step="1" placeholder="Ej: 3"
                value={landings} onChange={e => setLandings(e.target.value)}
                style={inputStyle} />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                              display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <CreditCard style={{ width: 13, height: 13 }} /> Forma de pago *
              </label>
              <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={inputStyle}>
                <option value="">— Seleccioná la forma de pago —</option>
                {PAYMENT_OPTIONS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                              display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <StickyNote style={{ width: 13, height: 13 }} /> Observaciones — opcional
              </label>
              <textarea rows={2} placeholder="Novedad del vuelo, clima, etc."
                value={notes} onChange={e => setNotes(e.target.value)}
                style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }} />
            </div>

            {saved && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                         borderRadius: 12, background: 'rgba(22,163,74,0.1)',
                         border: '1px solid rgba(22,163,74,0.25)' }}>
                <span style={{ fontSize: 16 }}>✅</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>
                  ¡Vuelo registrado!
                </span>
              </motion.div>
            )}
            {error && (
              <p style={{ margin: 0, fontSize: 12, color: '#ef4444', fontWeight: 600,
                          background: 'rgba(239,68,68,0.08)', padding: '8px 12px',
                          borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)' }}>
                {error}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={onDismiss}
                style={{ flex: 1, padding: '12px', borderRadius: 14,
                         border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                         background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
                         fontSize: 13, fontWeight: 700, color: sub }}>
                Después
              </button>
              <button onClick={handleSubmit} disabled={loading}
                style={{ flex: 2, padding: '12px', borderRadius: 14, border: 'none',
                         background: loading ? '#64748b' : 'linear-gradient(135deg,#2563eb,#1d4ed8)',
                         cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                         fontSize: 13, fontWeight: 700, color: 'white',
                         display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                {loading
                  ? <><Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Guardando...</>
                  : <><CheckCircle2 style={{ width: 14, height: 14 }} /> Registrar aterrizaje</>}
              </button>
            </div>

            {/* Acción destructiva: anular vuelo (clima, no salió a volar, etc.) */}
            {!annulOpen ? (
              <button
                type="button"
                onClick={() => { setAnnulOpen(true); setAnnulError(''); }}
                style={{ marginTop: 4, padding: '10px 12px', borderRadius: 12,
                         border: darkMode ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(239,68,68,0.25)',
                         background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
                         fontSize: 12, fontWeight: 700, color: '#ef4444',
                         display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                <Ban style={{ width: 13, height: 13 }} /> Anular vuelo
              </button>
            ) : (
              <div style={{ marginTop: 4, padding: '14px', borderRadius: 14,
                            background: darkMode ? 'rgba(239,68,68,0.06)' : 'rgba(254,226,226,0.45)',
                            border: '1px solid rgba(239,68,68,0.25)',
                            display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#ef4444',
                            display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Ban style={{ width: 13, height: 13 }} /> Anular este vuelo
                </p>
                <p style={{ margin: 0, fontSize: 12, color: sub, lineHeight: 1.5 }}>
                  Se marca como anulado: no cuenta para horas y desbloquea la aeronave.
                  El motivo queda registrado.
                </p>
                <textarea
                  rows={2}
                  placeholder="Motivo (ej: el vuelo se canceló por clima)"
                  value={annulReason}
                  onChange={e => { setAnnulReason(e.target.value); setAnnulError(''); }}
                  style={{ ...inputStyle, resize: 'none', lineHeight: 1.4, fontSize: 13, fontWeight: 500 }} />
                {annulError && (
                  <p style={{ margin: 0, fontSize: 11, color: '#ef4444', fontWeight: 600 }}>
                    {annulError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => { setAnnulOpen(false); setAnnulReason(''); setAnnulError(''); }}
                    disabled={annulLoading}
                    style={{ flex: 1, padding: '10px', borderRadius: 12,
                             border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                             background: 'transparent', cursor: annulLoading ? 'not-allowed' : 'pointer',
                             fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: sub }}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleAnnul}
                    disabled={annulLoading || !annulReason.trim()}
                    style={{ flex: 1, padding: '10px', borderRadius: 12, border: 'none',
                             background: (annulLoading || !annulReason.trim())
                               ? '#94a3b8'
                               : 'linear-gradient(135deg,#dc2626,#b91c1c)',
                             cursor: (annulLoading || !annulReason.trim()) ? 'not-allowed' : 'pointer',
                             fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: 'white',
                             display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    {annulLoading
                      ? <><Loader style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> Anulando…</>
                      : <><Ban style={{ width: 13, height: 13 }} /> Confirmar anulación</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </AnimatePresence>
  );
};

export default PostFlightModal;
